import path from "node:path";
import type { VideoMeta } from "./videoMeta";

/**
 * 视频号视频发布前的本地校验
 *
 * 平台限制：时长 8 小时内、大小不超过 20GB、格式为 MP4/H.264。
 * 在上传前拦截，避免把超限文件传完才被服务端拒绝。
 */

/** 时长上限：8 小时 */
export const MAX_DURATION_SECONDS = 8 * 60 * 60;
/** 大小上限：20GB */
export const MAX_FILE_SIZE = 20 * 1024 * 1024 * 1024;
/** 容器格式白名单 */
export const ALLOWED_EXTENSIONS = [".mp4"] as const;
/** 视频轨编码白名单（ISO BMFF sample entry fourcc）：H.264 */
export const ALLOWED_CODECS = ["avc1", "avc3"] as const;
/** 标题最少字符数（填了才校验） */
export const MIN_TITLE_LENGTH = 6;
/** 标题最多字符数 */
export const MAX_TITLE_LENGTH = 16;

function formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(2)} KB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
        return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    }
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDuration(seconds: number): string {
    const total = Math.round(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}小时${m}分${s}秒`;
    if (m > 0) return `${m}分${s}秒`;
    return `${s}秒`;
}

/**
 * 校验视频是否满足视频号限制
 *
 * @param filePath 视频文件路径（用于取扩展名和文件名做提示）
 * @param meta parseVideoMeta 的解析结果
 * @returns 不满足时返回中文错误提示，满足时返回 null
 */
export function validateShipinhaoVideo(filePath: string, meta: VideoMeta): string | null {
    const fileName = path.basename(filePath);
    const ext = path.extname(fileName).toLowerCase();

    // 1. 容器格式
    if (!ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])) {
        return `视频格式不支持：${fileName}。视频号仅支持 MP4/H.264 格式，请转换后重试。`;
    }

    // 2. 时长
    if (meta.duration > MAX_DURATION_SECONDS) {
        return `视频时长超过限制：${fileName}（${formatDuration(meta.duration)}）。视频号视频时长不能超过 8 小时，请剪辑后重试。`;
    }

    // 3. 文件大小
    if (meta.fileSize > MAX_FILE_SIZE) {
        return `视频大小超过限制：${fileName}（${formatFileSize(meta.fileSize)}）。视频号视频不能超过 20GB，请压缩后重试。`;
    }

    // 4. 编码格式。解析不到 codec 时不拦截，交给服务端判断，避免误伤
    if (meta.codec && !ALLOWED_CODECS.includes(meta.codec as (typeof ALLOWED_CODECS)[number])) {
        return `视频编码不支持：${fileName}（${meta.codec}）。视频号仅支持 H.264 编码，请转码后重试。`;
    }

    return null;
}

/**
 * 校验标题是否满足视频号限制
 *
 * 标题为可选参数：未传或为空白时不校验；填了就必须在 6-16 个字符之间。
 * 按 Unicode 码点计数，避免 emoji 等字符被算成多个。
 *
 * @param title 标题，可为空
 * @returns 不满足时返回中文错误提示，满足时返回 null
 */
export function validateShipinhaoTitle(title?: string): string | null {
    if (title === undefined) return null;

    const trimmed = title.trim();
    if (trimmed === "") return null;

    const length = [...trimmed].length;

    if (length < MIN_TITLE_LENGTH || length > MAX_TITLE_LENGTH) {
        return `视频号标题需要在 ${MIN_TITLE_LENGTH}-${MAX_TITLE_LENGTH} 个字符之间，当前 ${length} 个字符，请调整后重试。`;
    }

    return null;
}
